import React, { HTMLAttributes, useContext, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteMatch } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import TeamIcon from "@components/icons/TeamIcon";
import { AdditionalOfferInteractionTypeEnum } from "@data-types/analytics-tracker/AdditionalOfferInteractionTypeEnum";
import { DEFAULT_SECTION } from "@data-types/offer/event/EventViewStoreEnums";
import { TeamName } from "@features/offer/desktop/common";
import { OfferHistoryMatchResult } from "@features/offer/desktop/event-row/common/OfferHistoryMatchResult";
import {
	EventContext,
	LookupContext,
	OfferOptionsContext,
	OfferStoreContext,
	SportOfferContext,
} from "@gp/components";
import { LiveStreamStatus, Team } from "@gp/models";
import { SportOffer } from "@gp/offer";
import { history } from "@state";
import { THEME_SETTINGS } from "@theme-settings";
import { analyticsTracker } from "@utils/analytics";
import { buildRoute } from "@utils/common/buildOfferRoute";
import {
	useIsAdditionalOfferVisible,
	isAdditionalOfferOpenFor,
} from "@utils/offer";
import { getOfferHistoryScore } from "@utils/offer/offerHistoryScore";
import classNames from "classnames";
import { observer } from "mobx-react";

import {
	LiveStreamToggleButton,
	StatisticsButton,
	TvCoverageButton,
	WidgetToggleButton,
} from "../buttons";
import BetBuilderButton from "../buttons/BetBuilderButton";
import { EventFavoriteIcon } from "./EventFavoriteIcon";
import {
	AggregatedScore,
	LiveMatchResult,
	LiveMatchTime,
	RedCards,
	Score,
	StartTime,
} from "./common";

/**
 * Requires EventContext, LookupContext, SportOfferContext, OfferOptionsContext, EventContext
 */
export const EventDetails = observer(function EventDetails(props: {
	/** This is a flag not sport. */
	displaySport?: boolean;
	hasSecondaryOffer?: boolean;
	/** Is that specific event featured? Opened as additional offer. */
	isEventFeatured?: boolean;
	offerCategory?: string;
	isAdditionalOfferVisible?: boolean;
}) {
	const isAdditionalOfferVisible = useIsAdditionalOfferVisible(); // is ANY event selected/expanded

	return (
		<>
			<MatchTimeWrapper
				shouldDisplaySportIcon={props.displaySport}
				hasSecondaryOffer={props.hasSecondaryOffer}
				isEventFeatured={props.isEventFeatured}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
			<EventFavoriteIcon />
			<EventNamesWrapper
				shouldDisplaySportIcon={props.displaySport}
				offerCategory={props.offerCategory}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
			<MatchResultWrapper
				isOnHomepage={props.displaySport}
				isEventFeatured={props.isEventFeatured}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
			<ActionButtons
				isOnHomepage={props.displaySport}
				isEventFeatured={props.isEventFeatured}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
		</>
	);
});

const EventNamesWrapper = observer(function EventNamesWrapper(props: {
	shouldDisplaySportIcon?: boolean;
	offerCategory?: string;
	isAdditionalOfferVisible?: boolean;
}) {
	const { shouldDisplaySportIcon, offerCategory, isAdditionalOfferVisible } =
		props;

	return (
		<div className="offer__body__data offer__match">
			<TeamNames
				offerCategory={offerCategory}
				shouldDisplaySportIcon={shouldDisplaySportIcon}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
		</div>
	);
});

/**
 * Requires EventContext, LookupContext, SportOfferContext, OfferOptionsContext
 */
const TeamNames = observer(function TeamNames(props: {
	offerCategory?: string;
	shouldDisplaySportIcon?: boolean;
	isAdditionalOfferVisible?: boolean;
}) {
	const event = useContext(EventContext);
	const lookupContext = useContext(LookupContext);
	const sportContext = useContext(SportOfferContext);
	// @ts-expect-error
	const { eventIdsForEventSwitcher } = useContext(OfferStoreContext);

	// @ts-expect-error
	const { additionalOfferToggle } = useContext(OfferOptionsContext);

	const params = useParams<{
		culture: string;
		period: string;
		segments: string;
	}>();
	const liveMatch = useRouteMatch<{ period: string }>(
		"/:culture/live/:period"
	);

	let t1: Team | undefined, t2: Team | undefined;
	if (!event.isOutright) {
		t1 = lookupContext.teams.get(event.teamOneId || "");
		t2 = lookupContext.teams.get(event.teamTwoId || "");
	}

	let category = lookupContext.categories.get(event.sportCategoryId);
	let tournament = lookupContext.tournaments.get(event.tournamentId);

	if (
		(!event.isOutright && (t1 == null || t2 == null)) ||
		(event.isOutright && event.name == null)
	) {
		return null;
	}

	let link = buildRoute.eventCanonical({
		culture: params.culture,
		period: params.period,
		eventIds: event.id,
		teamOneAbrv: t1?.abrv,
		teamTwoAbrv: t2?.abrv,
	});

	if (event.isLive) {
		if (!window.location.pathname.includes("home")) {
			link = `/${params.culture}/live/${
				liveMatch?.params.period ?? DEFAULT_SECTION
			}/${event.id}`;
		}
	}

	const linkDataTip = `${category?.name} - ${tournament?.name}: ${
		event.isOutright ? `${event.name}` : `${t1?.name} - ${t2?.name}`
	}`;

	const linkToObject = {
		pathname: link,
		state: {
			prevUrl: history.location.pathname + history.location.search,
			eventIdsForEventSwitcher: eventIdsForEventSwitcher,
		},
	};

	let tooltipId = `tooltipTeams-${event.id}${
		props.offerCategory ? `-${props.offerCategory}` : ""
	}`;

	if (event.isLive) {
		tooltipId = "tooltipTeams-" + event.id + "-live";
	}

	const isLive = event.isLive;

	const { isAdditionalOfferVisible } = props;

	const elementAttributes: HTMLAttributes<HTMLElement> = {
		onClick: (e) => {
			if (event.isOutright) {
				e.preventDefault();
				return;
			}
			if (isLive || !isAdditionalOfferOpenFor(event.id)) {
				analyticsTracker.trackAdditionalOfferInteraction(
					isLive
						? AdditionalOfferInteractionTypeEnum.LiveAdditionalOffer
						: AdditionalOfferInteractionTypeEnum.PrematchAdditionalOffer,
					sportContext.abrv,
					t1?.abrv,
					t2?.abrv
				);
			}
			if (additionalOfferToggle && !isLive && !event.isOutright) {
				e.preventDefault();
				additionalOfferToggle(event);
			}
		},
		className: classNames("offer__competitors", {
			"offer__competitors--sml": isAdditionalOfferVisible,
			"u-anchor": !isLive && !event.isOutright,
		}),
		"data-tooltip-id": tooltipId,
		"data-tooltip-content": linkDataTip,
		"data-tooltip-position-strategy": "fixed",
	};

	return (
		<>
			<TeamNamesWrapper attributes={elementAttributes} to={linkToObject}>
				{event.isOutright ? (
					<div
						className={
							isLive
								? "offer__outright__name"
								: "u-color--text--primary"
						}
					>
						{event.name}
					</div>
				) : (
					<>
						<div className="offer__team">
							<TeamIcon
								showCountryFlag={tournament?.showCountryFlag}
								flagCode={t1?.countryCode}
								jerseyTypeAbrv={t1?.jerseyTypeAbrv}
								opponentFlagCode={t2?.countryCode}
								opponentJerseyTypeAbrv={t2?.jerseyTypeAbrv}
								className="offer__team__flag u-icon--base"
							/>
							<TeamName team={t1} />
							<RedCards
								sport={sportContext}
								statistics={
									event.result?.homeTeamTotalStatistics
								}
							/>
						</div>
						<div className="offer__team">
							<TeamIcon
								showCountryFlag={tournament?.showCountryFlag}
								flagCode={t2?.countryCode}
								jerseyTypeAbrv={t2?.jerseyTypeAbrv}
								opponentFlagCode={t1?.countryCode}
								opponentJerseyTypeAbrv={t1?.jerseyTypeAbrv}
								className="offer__team__flag u-icon--base"
							/>
							<TeamName team={t2} />
							<RedCards
								sport={sportContext}
								statistics={
									event.result?.awayTeamTotalStatistics
								}
							/>
						</div>
					</>
				)}
			</TeamNamesWrapper>

			{!isAdditionalOfferVisible && isLive && (
				<Tooltip
					id={tooltipId}
					className="at-tooltipTeams react-tooltip--primary"
					place="right-start"
					closeOnResize={true}
					offset={-100}
					style={{ maxWidth: "30%" }}
				/>
			)}
		</>
	);
});

const TeamNamesWrapper = function (props: {
	children: JSX.Element;
	attributes: HTMLAttributes<HTMLElement>;
	to: {
		pathname: string;
		state: {
			prevUrl: string;
			eventIdsForEventSwitcher: any;
		};
	};
}) {
	const { attributes, to, children } = props;

	return (
		<Link {...attributes} to={to}>
			{children}
		</Link>
	);
};

const MatchTimeWrapper = observer(function MatchTimeWrapper(props: {
	hasSecondaryOffer?: boolean;
	isEventFeatured?: boolean;
	shouldDisplaySportIcon?: boolean;
	isAdditionalOfferVisible?: boolean;
}) {
	const { hasSecondaryOffer, isEventFeatured, isAdditionalOfferVisible } =
		props;

	const event = useContext(EventContext);
	const options = useContext(OfferOptionsContext);

	const historyScore =
		// @ts-expect-error isHistoryEvent provided at runtime via SportsPageOptionsProvider, not on @gp/components OfferOptions type
		(options?.isHistoryEvent?.(event.id) ?? false)
			? getOfferHistoryScore(event.result)
			: null;

	if (event.isLive) {
		return (
			<LiveMatchTime
				event={event}
				className={classNames("offer__body__data offer__time", {
					"offer__time--sml": isAdditionalOfferVisible,
					"offer__time--stretch": hasSecondaryOffer,
					"is-selected": isEventFeatured,
					"is-top-event":
						//@ts-expect-error
						!options?.shouldHideTopTag && event.isTopEvent,
				})}
				isTopEvent={event.isTopEvent}
				isAdditionalOfferVisible={isAdditionalOfferVisible}
			/>
		);
	}

	return (
		<StartTime
			event={event}
			isAdditionalOfferVisible={isAdditionalOfferVisible}
			isAfterExtraTime={historyScore?.overTime != null}
			isAfterPenalties={historyScore?.penalties != null}
			className={classNames(
				{
					offer__body__data: !event.isOutright,
				},
				"offer__day",
				{
					"offer__day--sml": isAdditionalOfferVisible,
					"offer__day--stretch": hasSecondaryOffer,
					offer__outright__date: event.isOutright,
					"is-selected": isEventFeatured,
					"is-top-event":
						//@ts-expect-error
						!options?.shouldHideTopTag && event.isTopEvent,
				}
			)}
		/>
	);
});

export const MatchResultWrapper = observer(function MatchResultWrapper(props: {
	isOnHomepage?: boolean;
	/** Is this for the same event as is in additional offer. */
	isEventFeatured?: boolean;
	isAdditionalOfferVisible?: boolean;
}) {
	const event = useContext(EventContext);
	const sport = useContext(SportOfferContext);
	const offerOptionsContext = useContext(OfferOptionsContext);

	const { isOnHomepage, isEventFeatured, isAdditionalOfferVisible } = props;

	const isHistoryEvent =
		// @ts-expect-error
		offerOptionsContext.isHistoryEvent?.(event.id) ?? false;

	return (
		<>
			{!isAdditionalOfferVisible && !isHistoryEvent && (
				<AggregatedScore event={event} />
			)}
			{isHistoryEvent && (
				<OfferHistoryMatchResult
					event={event}
					sport={sport}
					isAdditionalOfferVisible={isAdditionalOfferVisible}
				/>
			)}
			{event.isLive && (
				<>
					{!isAdditionalOfferVisible && <Score />}
					<LiveMatchResult
						event={event}
						sport={sport}
						isEventFeatured={isEventFeatured}
						isAdditionalOfferVisible={isAdditionalOfferVisible}
						isOnHomepage={isOnHomepage}
					/>
				</>
			)}
		</>
	);
});

const ActionButtons = observer(function ActionButtons(props: {
	isOnHomepage?: boolean;
	isEventFeatured?: boolean;
	isAdditionalOfferVisible?: boolean;
}) {
	const event = useContext(EventContext);
	const offerOptionsContext = useContext(OfferOptionsContext);

	const sport = useContext(SportOfferContext) as SportOffer;
	// const { isLive } = useContext(OfferOptionsContext);

	const { isOnHomepage, isEventFeatured, isAdditionalOfferVisible } = props;

	if (
		// @ts-expect-error
		(offerOptionsContext.isHistoryEvent?.(event.id) ?? false) ||
		event.isOutright ||
		isAdditionalOfferVisible
	) {
		return null;
	}

	return (
		<div
			className={classNames("offer__body__data offer__actions", {
				"is-selected": isEventFeatured,
				"is-hidden": isAdditionalOfferVisible,
			})}
		>
			{WEBPACK_IS_BET_BUILDER_ENABLED && (
				<BetBuilderButton isOfferButton />
			)}

			<StatisticsButton isOfferButton />
			<TvCoverageButton isOfferButton isOnHomepage={isOnHomepage} />
			{/* IF STREAM IS AVAILABLE ON HOMEPAGE THEN RENDER STREAM INSTEAD OF LMT */}
			{THEME_SETTINGS.features.media.isLiveStreamEnabled && (
				<>
					{isOnHomepage ? (
						event?.liveStreamStatus &&
						event.liveStreamStatus !== LiveStreamStatus.Finished ? (
							<LiveStreamToggleButton
								isForSideWidget
								isOfferButton
							/>
						) : (
							<WidgetToggleButton isForSideWidget />
						)
					) : (
						<>
							<WidgetToggleButton isForSideWidget />
							<LiveStreamToggleButton
								isForSideWidget
								isOfferButton
							/>
						</>
					)}
				</>
			)}
		</div>
	);
});
